import React, { useState } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Input,
  Button,
} from '@fluentui/react-components';
import { ChevronDown20Regular, ChevronRight20Regular, Image20Regular, DocumentText20Regular, DocumentBulletList20Regular } from '@fluentui/react-icons';
import Header from '../components/Header';
import { formatBusinessDateTime } from '../lib/businessTime';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.padding('16px', '24px'),
    backgroundColor: '#5c5c5c',
    color: 'white',
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow8,
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '20px',
    fontWeight: 600,
    margin: 0,
  },
  filterSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  filterLabel: {
    fontSize: '14px',
    fontWeight: 500,
  },
  filterInput: {
    width: '300px',
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    ...shorthands.padding('24px'),
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow8,
    ...shorthands.borderRadius('4px'),
  },
  tableHeader: {
    backgroundColor: '#6b6b6b',
    color: 'white',
  },
  th: {
    ...shorthands.padding('12px', '16px'),
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: 600,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#7b7b7b',
    },
  },
  groupRow: {
    backgroundColor: '#e0e0e0',
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#d0d0d0',
    },
  },
  groupCell: {
    ...shorthands.padding('12px', '16px'),
    fontSize: '14px',
  },
  dataRow: {
    '&:nth-child(even)': {
      backgroundColor: tokens.colorNeutralBackground2,
    },
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  td: {
    ...shorthands.padding('12px', '16px'),
    fontSize: '13px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  addressLink: {
    color: tokens.colorBrandForeground1,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  iconButton: {
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    ...shorthands.borderRadius('4px'),
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  expandIcon: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '8px',
  },
});

interface Visit {
  id: string;
  address: string;
  coordinates: string;
  visitStartDate: Date;
  visitEndDate: Date;
  duration: string;
  tracker: string;
  driver?: string;
  hasPhotos: boolean;
  hasNotes: boolean;
}

interface GroupedVisits {
  address: string;
  visitCount: number;
  totalDuration: string;
  visits: Visit[];
  expanded: boolean;
}

export default function CustomerVisits() {
  const styles = useStyles();
  const [filter, setFilter] = useState('');
  const [sortColumn, setSortColumn] = useState<'startDate' | 'endDate'>('startDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Mock data for visits
  const [groupedVisits, setGroupedVisits] = useState<GroupedVisits[]>([
    {
      address: 'Sazón Criollo',
      visitCount: 15,
      totalDuration: '13d 27m',
      expanded: false,
      visits: [
        {
          id: '1',
          address: 'Sazón Criollo',
          coordinates: '18.467316, -67.036773',
          visitStartDate: new Date('2025-11-03T10:07:00'),
          visitEndDate: new Date('2025-11-03T10:26:00'),
          duration: '19m',
          tracker: 'Philly Mack Rolloff',
          driver: undefined,
          hasPhotos: true,
          hasNotes: true,
        },
        {
          id: '2',
          address: 'Sazón Criollo',
          coordinates: '18.467316, -67.036773',
          visitStartDate: new Date('2025-11-03T14:05:00'),
          visitEndDate: new Date('2025-11-04T03:37:00'),
          duration: '13h 33m',
          tracker: 'X2A-YKI BLUE MACK',
          driver: undefined,
          hasPhotos: true,
          hasNotes: true,
        },
        {
          id: '3',
          address: 'Sazón Criollo',
          coordinates: '18.467316, -67.036773',
          visitStartDate: new Date('2025-11-04T07:29:00'),
          visitEndDate: new Date('2025-11-05T05:16:00'),
          duration: '21h 47m',
          tracker: 'X2A-YKI BLUE MACK',
          driver: undefined,
          hasPhotos: true,
          hasNotes: true,
        },
        {
          id: '4',
          address: 'Sazón Criollo',
          coordinates: '18.467316, -67.036773',
          visitStartDate: new Date('2025-11-05T08:34:00'),
          visitEndDate: new Date('2025-11-06T05:08:00'),
          duration: '20h 34m',
          tracker: 'X2A-YKI BLUE MACK',
          driver: undefined,
          hasPhotos: true,
          hasNotes: true,
        },
        {
          id: '5',
          address: 'Sazón Criollo',
          coordinates: '18.467316, -67.036773',
          visitStartDate: new Date('2025-11-06T05:41:00'),
          visitEndDate: new Date('2025-11-07T01:31:00'),
          duration: '19h 50m',
          tracker: 'X2A-YKI BLUE MACK',
          driver: undefined,
          hasPhotos: true,
          hasNotes: true,
        },
        {
          id: '6',
          address: 'Sazón Criollo',
          coordinates: '18.467316, -67.036773',
          visitStartDate: new Date('2025-11-07T06:24:00'),
          visitEndDate: new Date('2025-11-10T05:05:00'),
          duration: '2d 22h 41m',
          tracker: 'X2A-YKI BLUE MACK',
          driver: undefined,
          hasPhotos: true,
          hasNotes: true,
        },
        {
          id: '7',
          address: 'Sazón Criollo',
          coordinates: '18.467316, -67.036773',
          visitStartDate: new Date('2025-11-10T09:04:00'),
          visitEndDate: new Date('2025-11-10T09:58:00'),
          duration: '54m',
          tracker: '5CB-X2P GRAPPLER',
          driver: 'Damian H.',
          hasPhotos: true,
          hasNotes: true,
        },
        {
          id: '8',
          address: 'Sazón Criollo',
          coordinates: '18.467316, -67.036773',
          visitStartDate: new Date('2025-11-10T14:40:00'),
          visitEndDate: new Date('2025-11-11T05:16:00'),
          duration: '14h 36m',
          tracker: 'Philly Mack Rolloff',
          driver: undefined,
          hasPhotos: true,
          hasNotes: true,
        },
        {
          id: '9',
          address: 'Sazón Criollo',
          coordinates: '18.467316, -67.036773',
          visitStartDate: new Date('2025-11-11T08:14:00'),
          visitEndDate: new Date('2025-11-12T07:40:00'),
          duration: '23h 26m',
          tracker: 'X2A-YKI BLUE MACK',
          driver: undefined,
          hasPhotos: true,
          hasNotes: true,
        },
        {
          id: '10',
          address: 'Sazón Criollo',
          coordinates: '18.467316, -67.036773',
          visitStartDate: new Date('2025-11-12T09:12:00'),
          visitEndDate: new Date('2025-11-12T09:31:00'),
          duration: '19m',
          tracker: 'Philly Mack Rolloff',
          driver: undefined,
          hasPhotos: true,
          hasNotes: true,
        },
        {
          id: '11',
          address: 'Sazón Criollo',
          coordinates: '18.467316, -67.036773',
          visitStartDate: new Date('2025-11-12T11:03:00'),
          visitEndDate: new Date('2025-11-12T11:19:00'),
          duration: '16m',
          tracker: 'Philly Mack Rolloff',
          driver: undefined,
          hasPhotos: true,
          hasNotes: true,
        },
        {
          id: '12',
          address: 'Sazón Criollo',
          coordinates: '18.467316, -67.036773',
          visitStartDate: new Date('2025-11-12T12:09:00'),
          visitEndDate: new Date('2025-11-13T05:22:00'),
          duration: '17h 13m',
          tracker: 'Philly Mack Rolloff',
          driver: undefined,
          hasPhotos: true,
          hasNotes: true,
        },
      ],
    },
  ]);

  const formatDateTime = (date: Date) => {
    return formatBusinessDateTime(date.toISOString());
  };

  const toggleGroup = (index: number) => {
    setGroupedVisits(prev =>
      prev.map((group, i) =>
        i === index ? { ...group, expanded: !group.expanded } : group
      )
    );
  };

  const expandAll = () => {
    setGroupedVisits(prev => prev.map(group => ({ ...group, expanded: true })));
  };

  const handleSort = (column: 'startDate' | 'endDate') => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleCoordinateClick = (coordinates: string) => {
    const [lat, lng] = coordinates.split(', ').map(parseFloat);
    // TODO: Navigate to map view with these coordinates
    console.log('Navigate to coordinates:', lat, lng);
  };

  return (
    <div className={styles.container}>
        <table className={styles.table}>
          <thead className={styles.tableHeader}>
            <tr>
              <th className={styles.th}>Address</th>
              <th className={styles.th} onClick={() => handleSort('startDate')}>
                Visit Start Date {sortColumn === 'startDate' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th className={styles.th} onClick={() => handleSort('endDate')}>
                Visit End Date {sortColumn === 'endDate' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th className={styles.th}>Visit Duration</th>
              <th className={styles.th}>Tracker</th>
              <th className={styles.th}>Driver</th>
              <th className={styles.th}>Photos</th>
              <th className={styles.th}>Visit Notes</th>
              <th className={styles.th}>Visit Summary</th>
            </tr>
          </thead>
          <tbody>
            {groupedVisits.map((group, groupIndex) => (
              <React.Fragment key={groupIndex}>
                {/* Group Header Row */}
                <tr className={styles.groupRow} onClick={() => toggleGroup(groupIndex)}>
                  <td className={styles.groupCell}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span className={styles.expandIcon}>
                        {group.expanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                      </span>
                      {group.address} ({group.visitCount} Visits)
                    </div>
                  </td>
                  <td className={styles.groupCell}></td>
                  <td className={styles.groupCell}></td>
                  <td className={styles.groupCell}>{group.totalDuration}</td>
                  <td className={styles.groupCell}></td>
                  <td className={styles.groupCell}></td>
                  <td className={styles.groupCell}></td>
                  <td className={styles.groupCell}></td>
                  <td className={styles.groupCell}>
                    <span className={styles.expandIcon}>
                      {group.expanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                    </span>
                  </td>
                </tr>

                {/* Visit Rows (shown when expanded) */}
                {group.expanded && group.visits.map((visit) => (
                  <tr key={visit.id} className={styles.dataRow}>
                    <td className={styles.td}>
                      <a
                        className={styles.addressLink}
                        onClick={() => handleCoordinateClick(visit.coordinates)}
                      >
                        {visit.coordinates}
                      </a>
                    </td>
                    <td className={styles.td}>{formatDateTime(visit.visitStartDate)}</td>
                    <td className={styles.td}>{formatDateTime(visit.visitEndDate)}</td>
                    <td className={styles.td}>{visit.duration}</td>
                    <td className={styles.td}>{visit.tracker}</td>
                    <td className={styles.td}>{visit.driver || ''}</td>
                    <td className={styles.td}>
                      <div className={styles.iconButton}>
                        <Image20Regular />
                      </div>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.iconButton}>
                        <DocumentText20Regular />
                      </div>
                    </td>
                    <td className={styles.td}>
                      <div className={styles.iconButton}>
                        <DocumentBulletList20Regular />
                      </div>
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
  );
}
